import type { Distance, SpatialRectangle } from "@pdfking/math"
import { Scale, Subtract, Total } from "@pdfking/math"
import type { PageShape } from "@pdfking/renderer-api"
import { HorizontalAlign, PlotFunction } from "@pdfking/renderer-api"

import { PALETTE } from "#constants"
import type { ContentLabels } from "#internal"

import { PlotNotebookBackground } from "../layout/PlotNotebookBackground"
import { CreateMeasure } from "../text/CreateMeasure"
import { PlainText } from "../text/PlainText"
import { NotebookBackground } from "../types/NotebookBackground"

const MEASURE = CreateMeasure()

// The lines, the heading's the first, each a line and a half of the
// agenda's apart
const LINES = 13
const ROW = 1.5

/** An agenda's personal details, on its first page as the shop's templates
 * have them: at the foot of `frame`, LINES lines `pitch` and a half apart,
 * the first under the heading, set on a badge of the primary on its right; the
 * three after it for the name, then the home's, the office's, the phone's,
 * the mobile's and the e-mail's, each named on its line, a free one between
 * each. Its words `labels`. */
export const DrawAgendaInfo = ({
  frame,
  labels,
  pitch,
}: {
  frame: SpatialRectangle<Distance>
  labels: ContentLabels
  pitch: Distance
}): PageShape[] => {
  const row = Scale(pitch, ROW)
  const top = Total(frame.y, Scale(row, LINES))
  const line = (i: number) => Subtract(top, Scale(row, i))
  // The heading on its badge, standing on the first line
  const heading = labels.PERSONAL_INFO()
  const font = Scale(row, 0.5)
  const pad = Scale(font, 0.4)
  const badge_width = Total(MEASURE(heading, font), pad, pad)
  const badge = {
    height: Scale(row, 0.8),
    width: badge_width,
    x: Subtract(Total(frame.x, frame.width), badge_width),
    y: line(1),
  }
  // Each named on the line it is written on, past the name's three
  const named = [labels.HOME(), labels.OFFICE(), labels.PHONE(), labels.MOBILE(), labels.EMAIL()]
  const label = Scale(row, 0.3)
  return [
    ...PlotNotebookBackground({
      background: NotebookBackground.LINES,
      box: { ...frame, height: Subtract(top, frame.y), y: frame.y },
      pitch: row,
    }),
    { enum: PlotFunction.PLOT_RECT, fill_color: PALETTE.primary, position: badge },
    PlainText(
      heading,
      badge.x,
      badge.width,
      Total(badge.y, Scale(Subtract(badge.height, font), 0.5)),
      font,
      PALETTE.paper,
      HorizontalAlign.CENTER
    ),
    ...named.map((name, i) =>
      PlainText(
        name,
        frame.x,
        frame.width,
        Total(line(5 + 2 * i), Scale(row, 0.2)),
        label,
        PALETTE.lines
      )
    ),
  ]
}
